import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { format, parseISO } from "date-fns";
import { Droplets, Heart } from "lucide-react";
import { Popover, PopoverContent, SYMPTOM_OPTIONS, getPhaseBandOption } from "@nfp-tracker/components";
import { cn, formatTemperature } from "@nfp-tracker/utils";
import {
  EMPTY_TEMPERATURE_HEIGHT,
  GUTTER_WIDTH,
  HEADER_HEIGHT,
  LANE_HEIGHT,
  MONITOR_BADGE_CLASS,
  PHASE_STRIP_HEIGHT,
  PLOT_PAD_Y,
  overlayColor,
} from "./cycleChartStyles";
import {
  LANE_LABELS,
  computeTemperatureTicks,
  describeChartDay,
  describeOverlayColumn,
  describeOverlayX,
  describePhaseKeys,
  formatOverlayX,
  monitorLabel,
  overlayColumnCount,
  type CycleChartDay,
  type CycleChartModel,
  type LaneKey,
  type OverlayModel,
} from "./cycleChartData";

/**
 * Hand-drawn SVG rather than recharts: every lane under the temperature line has to sit in
 * exactly the same pixel column as its temperature dot, and the Y axis / lane labels have to
 * stay put while long cycles scroll sideways. Owning the x scale (one fixed-width column per
 * day) gives both for free; with recharts the plot's internal margins and axis would scroll
 * away and drift from the HTML lanes.
 */

const TEMP_STROKE = "stroke-emerald-600 dark:stroke-emerald-400";
const TEMP_FILL = "fill-emerald-600 dark:fill-emerald-400";
const COVER_STROKE = "stroke-sky-600 dark:stroke-sky-400";
const COVER_TEXT = "fill-sky-700 dark:fill-sky-300";
const SHIFT_STROKE = "stroke-orange-500 dark:stroke-orange-400";
const SHIFT_TEXT = "fill-orange-600 dark:fill-orange-300";

/** Phase strip shows only the ranges people read the chart for; menses is the bleeding lane. */
const STRIP_PHASES = ["fertile", "peak", "postPeak"] as const;

type YScale = (temperature: number) => number;

const makeYScale = ([lo, hi]: [number, number], height: number): YScale => {
  const inner = Math.max(1, height - PLOT_PAD_Y * 2);
  return (t) => PLOT_PAD_Y + ((hi - t) / (hi - lo || 1)) * inner;
};

/** Path through consecutive points; jumps over a missing day are drawn as a separate faint dashed path. */
const buildLinePaths = (
  points: { column: number; temperature: number }[],
  x: (column: number) => number,
  y: YScale
) => {
  let solid = "";
  let gaps = "";
  points.forEach((point, index) => {
    const px = x(point.column);
    const py = y(point.temperature);
    if (index === 0) {
      solid += `M ${px} ${py}`;
      return;
    }
    const prev = points[index - 1];
    if (point.column - prev.column === 1) {
      solid += ` L ${px} ${py}`;
    } else {
      gaps += ` M ${x(prev.column)} ${y(prev.temperature)} L ${px} ${py}`;
      solid += ` M ${px} ${py}`;
    }
  });
  return { solid, gaps };
};

// ─── Lane marks ──────────────────────────────────────────────────────────────────────────────

const BLEEDING_BAR_CLASS = (code: number, rank: number) => {
  if (code === 5) return "bg-amber-800 dark:bg-amber-700"; // brown spotting: colour, not volume
  if (rank <= 0.5) return "bg-red-300 dark:bg-red-300/80";
  if (rank <= 1) return "bg-red-400";
  if (rank <= 2) return "bg-red-600";
  return "bg-red-800 dark:bg-red-700";
};

export const OpkDiamond = ({ positive }: { positive: boolean }) => (
  <svg viewBox="0 0 12 12" className="h-3 w-3 flex-none" aria-hidden>
    <path
      d="M6 0.8 L11.2 6 L6 11.2 L0.8 6 Z"
      className={cn(
        "stroke-fuchsia-600 dark:stroke-fuchsia-400",
        positive ? "fill-fuchsia-600 dark:fill-fuchsia-400" : "fill-background"
      )}
      strokeWidth={1.6}
    />
  </svg>
);

export const MucusMark = ({ kind }: { kind: NonNullable<CycleChartDay["mucus"]>["kind"] }) => {
  if (kind === "dry") {
    return <span className="block h-1.5 w-1.5 flex-none rounded-full border border-muted-foreground/60" />;
  }
  const chip =
    kind === "peak"
      ? "bg-green-600 ring-2 ring-green-600/30"
      : kind === "post-peak"
        ? "bg-slate-400"
        : "bg-teal-500";
  return (
    <span className={cn("inline-flex h-4 w-4 flex-none items-center justify-center rounded-full text-white", chip)}>
      <Droplets className="h-2.5 w-2.5" />
    </span>
  );
};

export const MonitorBadge = ({ status }: { status: NonNullable<CycleChartDay["monitor"]> }) => (
  <span
    className={cn(
      "inline-flex h-5 min-w-5 flex-none items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none",
      MONITOR_BADGE_CLASS[status]
    )}
  >
    {status.charAt(0).toUpperCase()}
  </span>
);

const LaneCell = ({ lane, day }: { lane: LaneKey; day: CycleChartDay }) => {
  switch (lane) {
    case "monitor":
      return day.monitor ? <MonitorBadge status={day.monitor} /> : null;
    case "opk":
      return day.opk ? <OpkDiamond positive={day.opk === "positive"} /> : null;
    case "mucus":
      return day.mucus ? <MucusMark kind={day.mucus.kind} /> : null;
    case "bleeding":
      return day.bleeding ? (
        <span className="flex h-full w-full items-end justify-center pb-1">
          <span
            className={cn("block w-2.5 rounded-sm", BLEEDING_BAR_CLASS(day.bleeding.code, day.bleeding.rank))}
            style={{ height: `${Math.max(4, (day.bleeding.rank / 3) * (LANE_HEIGHT - 8))}px` }}
          />
        </span>
      ) : null;
    case "intercourse":
      return day.intercourse ? (
        <Heart className="h-3.5 w-3.5 fill-current text-pink-700 dark:text-pink-400" />
      ) : null;
    case "symptoms": {
      if (!day.symptoms.length) return null;
      const option = SYMPTOM_OPTIONS.find((candidate) => candidate.name === day.symptoms[0]);
      const Icon = option?.icon;
      return (
        <span className="relative inline-flex items-center">
          {Icon ? (
            <Icon className={cn("h-3.5 w-3.5", option.color)} />
          ) : (
            <span className="block h-2 w-2 rounded-full bg-amber-500" />
          )}
          {day.symptoms.length > 1 ? (
            <span className="absolute -right-2 -top-1.5 text-[8px] font-semibold text-muted-foreground">
              +{day.symptoms.length - 1}
            </span>
          ) : null}
        </span>
      );
    }
  }
};

// ─── Frame shared by both charts ─────────────────────────────────────────────────────────────

interface ChartColumn {
  key: string;
  header: string;
  headerClassName?: string;
  ariaLabel: string;
}

type ActiveSource = "hover" | "focus" | "pin";

/** Lets taps fall through Radix's popper wrapper (which takes no props) to the chart below. */
const passThroughWrapper = (node: HTMLDivElement | null) => {
  if (node?.parentElement?.hasAttribute("data-radix-popper-content-wrapper")) {
    node.parentElement.style.pointerEvents = "none";
  }
};

interface ChartFrameProps {
  columns: ChartColumn[];
  columnWidth: number;
  temperatureHeight: number;
  temperatureDomain: [number, number] | null;
  /**
   * Shown instead of the plot. `fill` keeps the full plot height (so switching into an empty
   * state doesn't resize the card); `compact` is a one-line row; null hides the row.
   */
  temperatureEmpty?: { text: string; size: "fill" | "compact" } | null;
  showPhaseStrip: boolean;
  phaseStrip?: React.ReactNode;
  washes?: React.ReactNode;
  plot?: (x: (column: number) => number, y: YScale, activeIndex: number | null) => React.ReactNode;
  lanes?: { key: LaneKey; render: (column: number) => React.ReactNode }[];
  renderTooltip: (index: number) => React.ReactNode;
  defaultFocusIndex: number;
  scrollRef?: React.Ref<HTMLDivElement>;
  groupLabel: string;
  /** Gutter label for the header row ("CD", or "Date" when day numbers aren't cycle days). */
  headerLabel?: string;
  className?: string;
}

const ChartFrame = ({
  columns,
  columnWidth,
  temperatureHeight,
  temperatureDomain,
  temperatureEmpty,
  showPhaseStrip,
  phaseStrip,
  washes,
  plot,
  lanes = [],
  renderTooltip,
  defaultFocusIndex,
  scrollRef,
  groupLabel,
  headerLabel = "CD",
  className,
}: ChartFrameProps) => {
  const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const groupRef = React.useRef<HTMLDivElement | null>(null);
  const [active, setActive] = React.useState<{ index: number; source: ActiveSource } | null>(null);
  const activeIndex = active && active.index < columns.length ? active.index : null;

  const width = columns.length * columnWidth;
  const hasPlot = Boolean(temperatureDomain && plot);
  const tempRowHeight = hasPlot
    ? temperatureHeight
    : temperatureEmpty
      ? temperatureEmpty.size === "fill"
        ? temperatureHeight
        : EMPTY_TEMPERATURE_HEIGHT
      : 0;
  const y = temperatureDomain ? makeYScale(temperatureDomain, temperatureHeight) : null;
  const x = (column: number) => column * columnWidth + columnWidth / 2;
  const ticks = temperatureDomain ? computeTemperatureTicks(temperatureDomain) : [];
  const focusIndex = activeIndex ?? Math.min(Math.max(0, defaultFocusIndex), columns.length - 1);
  // Beside the column on wide screens; above it on phones, where there's no room at the side
  // and top/bottom placement can slide sideways to stay on screen.
  const narrow =
    typeof window !== "undefined" && Boolean(window.matchMedia?.("(max-width: 639px)").matches);
  const bodyTop = HEADER_HEIGHT + (showPhaseStrip ? PHASE_STRIP_HEIGHT : 0);

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    const moves: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: columns.length - 1,
    };
    if (event.key in moves) {
      event.preventDefault();
      const next = Math.max(0, Math.min(columns.length - 1, moves[event.key]));
      buttonRefs.current[next]?.focus();
    }
  };

  const handleBlur = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (next && groupRef.current?.contains(next)) return;
    setActive((prev) => (prev?.source === "focus" ? null : prev));
  };

  return (
    <div className={cn("flex min-w-0 rounded-lg border bg-card", className)}>
      {/* Fixed gutter: stays put while the days scroll. */}
      <div className="flex-none border-r" style={{ width: GUTTER_WIDTH }} aria-hidden>
        <div
          className="flex items-center px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
          style={{ height: HEADER_HEIGHT }}
        >
          {headerLabel}
        </div>
        {showPhaseStrip ? <div style={{ height: PHASE_STRIP_HEIGHT }} /> : null}
        {tempRowHeight ? (
          <div className="relative" style={{ height: tempRowHeight }}>
            {hasPlot && y ? (
              <svg width={GUTTER_WIDTH} height={temperatureHeight} className="block overflow-visible">
                <text x={6} y={10} className="fill-muted-foreground text-[9px]">
                  °F
                </text>
                {ticks.map((tick) => (
                  <text
                    key={tick}
                    x={GUTTER_WIDTH - 6}
                    y={y(tick)}
                    textAnchor="end"
                    dominantBaseline="middle"
                    className="fill-muted-foreground text-[10px] tabular-nums"
                  >
                    {formatTemperature(tick)}
                  </text>
                ))}
              </svg>
            ) : (
              <div className="flex h-full items-center px-2 text-[11px] font-medium text-muted-foreground">
                Temp
              </div>
            )}
          </div>
        ) : null}
        {lanes.map((lane) => (
          <div
            key={lane.key}
            className="flex items-center whitespace-nowrap border-t px-1.5 text-[10px] font-medium text-muted-foreground"
            style={{ height: LANE_HEIGHT }}
          >
            {LANE_LABELS[lane.key]}
          </div>
        ))}
      </div>

      <div ref={scrollRef} className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain">
        <Popover open={activeIndex !== null} onOpenChange={(open) => !open && setActive(null)}>
          <div className="relative" style={{ width }}>
            {/* Background ranges, behind everything below the header. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0" style={{ top: bodyTop }} aria-hidden>
              {washes}
            </div>

            <div className="relative flex" style={{ height: HEADER_HEIGHT }} aria-hidden>
              {columns.map((column) => (
                <div
                  key={column.key}
                  className="flex flex-none items-center justify-center"
                  style={{ width: columnWidth }}
                >
                  <span
                    className={cn(
                      "px-1 text-[10px] font-semibold tabular-nums text-muted-foreground",
                      column.headerClassName
                    )}
                  >
                    {column.header}
                  </span>
                </div>
              ))}
            </div>

            {showPhaseStrip ? (
              <div className="relative" style={{ height: PHASE_STRIP_HEIGHT }} aria-hidden>
                {phaseStrip}
              </div>
            ) : null}

            {tempRowHeight ? (
              <div className="relative" style={{ height: tempRowHeight }} aria-hidden>
                {hasPlot && y && plot ? (
                  <svg width={width} height={temperatureHeight} className="block">
                    {ticks.map((tick) => (
                      <line
                        key={tick}
                        x1={0}
                        x2={width}
                        y1={y(tick)}
                        y2={y(tick)}
                        className="stroke-border/70"
                        strokeDasharray="1 5"
                      />
                    ))}
                    {plot(x, y, activeIndex)}
                  </svg>
                ) : (
                  <div className="flex h-full items-center justify-center px-3 text-xs text-muted-foreground">
                    {temperatureEmpty?.text}
                  </div>
                )}
              </div>
            ) : null}

            {lanes.map((lane) => (
              <div key={lane.key} className="relative flex border-t" style={{ height: LANE_HEIGHT }} aria-hidden>
                {columns.map((column, index) => (
                  <div
                    key={column.key}
                    className="flex flex-none items-center justify-center"
                    style={{ width: columnWidth }}
                  >
                    {lane.render(index)}
                  </div>
                ))}
              </div>
            ))}

            {activeIndex !== null ? (
              <PopoverPrimitive.Anchor asChild>
                <div
                  key={activeIndex}
                  className="pointer-events-none absolute"
                  style={{ left: activeIndex * columnWidth, width: columnWidth, top: bodyTop, bottom: 0 }}
                  aria-hidden
                />
              </PopoverPrimitive.Anchor>
            ) : null}

            {/* One focusable column per day: hover, tap or arrow keys to inspect. */}
            <div
              ref={groupRef}
              role="group"
              aria-label={groupLabel}
              className="absolute inset-0 flex"
              onMouseLeave={() => setActive((prev) => (prev?.source === "hover" ? null : prev))}
            >
              {columns.map((column, index) => (
                <button
                  key={column.key}
                  ref={(node) => {
                    buttonRefs.current[index] = node;
                  }}
                  type="button"
                  tabIndex={index === focusIndex ? 0 : -1}
                  aria-label={column.ariaLabel}
                  onMouseEnter={() => setActive({ index, source: "hover" })}
                  onFocus={() =>
                    setActive((prev) => (prev?.index === index && prev.source === "pin" ? prev : { index, source: "focus" }))
                  }
                  onBlur={handleBlur}
                  onClick={() => setActive({ index, source: "pin" })}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  className={cn(
                    "h-full flex-none touch-manipulation bg-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                    activeIndex === index && "bg-foreground/[0.06] outline outline-1 -outline-offset-1 outline-foreground/20"
                  )}
                  style={{ width: columnWidth }}
                />
              ))}
            </div>
          </div>

          <PopoverContent
            side={narrow ? "top" : "right"}
            align={narrow ? "center" : "start"}
            sideOffset={6}
            collisionPadding={8}
            // Info only: focus stays on the day column, and the pointer passes straight through.
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            // Moving between day columns (pointer or keyboard) retargets the tooltip rather
            // than dismissing it; anything else outside the chart dismisses it.
            onInteractOutside={(event) => {
              if (groupRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
            aria-hidden
            // Inline style, not just the class: inside a modal Dialog, Radix's dismissable layer
            // sets an inline pointer-events:auto on the content (only props.style overrides it),
            // and its positioning wrapper is a hit target of its own. Either would swallow taps
            // on the days underneath the tooltip.
            style={{ pointerEvents: "none" }}
            ref={passThroughWrapper}
            className="pointer-events-none w-56 p-2.5 text-xs data-[state=open]:animate-none"
          >
            {activeIndex !== null ? renderTooltip(activeIndex) : null}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

// ─── Tooltip bodies ──────────────────────────────────────────────────────────────────────────

const TooltipRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-2">
    <dt className="w-[4.5rem] flex-none text-muted-foreground">{label}</dt>
    <dd className="flex min-w-0 flex-1 items-center gap-1.5">{children}</dd>
  </div>
);

export const DayTooltip = ({ day, model }: { day: CycleChartDay; model: CycleChartModel }) => {
  const phases = describePhaseKeys(day.phases);
  const isShift = model.thermalShiftCd === day.cycleDay;
  return (
    <div className="space-y-1.5">
      <div>
        <p className="font-semibold">
          {model.partial ? "" : `CD ${day.cycleDay} · `}
          {format(parseISO(day.date), "EEE, MMM d")}
        </p>
        {phases.length ? <p className="text-[11px] text-muted-foreground">{phases.join(" · ")}</p> : null}
      </div>
      {day.hasObservation ? (
        <dl className="space-y-1">
          {day.temperature !== null ? (
            <TooltipRow label="Temp">
              <span className="font-medium tabular-nums">{formatTemperature(day.temperature, "°F")}</span>
              {isShift ? <span className="text-orange-600 dark:text-orange-300">shift starts</span> : null}
            </TooltipRow>
          ) : null}
          {day.monitor ? (
            <TooltipRow label="Monitor">
              <MonitorBadge status={day.monitor} />
              {monitorLabel(day.monitor)}
            </TooltipRow>
          ) : null}
          {day.opk ? (
            <TooltipRow label="OPK">
              <OpkDiamond positive={day.opk === "positive"} />
              {day.opk === "positive" ? "Positive" : "Negative"}
            </TooltipRow>
          ) : null}
          {day.mucus ? (
            <TooltipRow label="Mucus">
              <MucusMark kind={day.mucus.kind} />
              <span className="min-w-0">
                {day.mucus.label}
                {day.mucus.kind === "peak" ? " · peak-type" : day.mucus.kind === "post-peak" ? " · past Peak + 3" : ""}
              </span>
            </TooltipRow>
          ) : null}
          {day.bleeding ? <TooltipRow label="Bleeding">{day.bleeding.label}</TooltipRow> : null}
          {day.intercourse ? <TooltipRow label="Intercourse">Yes</TooltipRow> : null}
          {day.symptoms.length ? <TooltipRow label="Symptoms">{day.symptoms.join(", ")}</TooltipRow> : null}
          {day.notes ? (
            <TooltipRow label="Notes">
              <span className="line-clamp-2 min-w-0 text-muted-foreground">{day.notes}</span>
            </TooltipRow>
          ) : null}
        </dl>
      ) : (
        <p className="text-muted-foreground">No entry for this day.</p>
      )}
    </div>
  );
};

// ─── Single cycle ────────────────────────────────────────────────────────────────────────────

const spanStyle = (startCd: number, endCd: number, columnWidth: number): React.CSSProperties => ({
  left: (startCd - 1) * columnWidth,
  width: (endCd - startCd + 1) * columnWidth,
});

interface SingleProps {
  model: CycleChartModel;
  columnWidth: number;
  temperatureHeight: number;
  /** Draw the cover line and the temperature shift (first 3 high temps). */
  showThermalMarkers: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
  className?: string;
}

export const SingleCycleChart = ({
  model,
  columnWidth,
  temperatureHeight,
  showThermalMarkers,
  scrollRef,
  className,
}: SingleProps) => {
  const columns: ChartColumn[] = model.days.map((day) => ({
    key: String(day.cycleDay),
    // A partial cycle's day numbers are offsets from the first logged day, not cycle days.
    header: model.partial ? format(parseISO(day.date), "d") : String(day.cycleDay),
    headerClassName:
      day.cycleDay === model.peakCd
        ? "rounded-sm bg-violet-700 text-white"
        : day.cycleDay === model.todayCd
          ? "rounded-sm ring-1 ring-primary text-foreground"
          : undefined,
    ariaLabel: `${day.cycleDay === model.todayCd ? "Today. " : ""}${describeChartDay(day, model)}`,
  }));

  const fertile = model.spans.find((span) => span.key === "fertile");
  const peak = model.spans.find((span) => span.key === "peak");

  const washes = (
    <>
      {fertile ? (
        <div
          className={cn(
            "absolute inset-y-0 bg-green-500/10 dark:bg-green-400/10",
            fertile.open && "border-r-2 border-dashed border-green-600/40"
          )}
          style={spanStyle(fertile.startCd, fertile.endCd, columnWidth)}
        />
      ) : null}
      {peak ? (
        <div
          className="absolute inset-y-0 bg-violet-600/15 dark:bg-violet-400/15"
          style={spanStyle(peak.startCd, peak.endCd, columnWidth)}
        />
      ) : null}
    </>
  );

  const stripSpans = STRIP_PHASES.flatMap((key) => model.spans.filter((span) => span.key === key));
  const phaseStrip = stripSpans.map((span) => (
    <div
      key={`${span.key}-${span.startCd}`}
      className={cn(
        "absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full",
        getPhaseBandOption(span.key)?.colorClass,
        span.open && "opacity-60"
      )}
      style={spanStyle(span.startCd, span.endCd, columnWidth)}
    />
  ));

  const tempPoints = model.days.flatMap((day, index) =>
    day.temperature !== null ? [{ column: index, temperature: day.temperature, cycleDay: day.cycleDay }] : []
  );
  const shiftCd = showThermalMarkers ? model.thermalShiftCd : null;
  const isHighTemp = (cd: number) => shiftCd !== null && cd >= shiftCd && cd <= shiftCd + 2;

  const plot = (x: (column: number) => number, y: YScale, activeIndex: number | null) => {
    const { solid, gaps } = buildLinePaths(tempPoints, x, y);
    const plotWidth = model.length * columnWidth;
    const coverLine = showThermalMarkers ? model.coverLine : null;
    return (
      <>
        {coverLine ? (
          <g>
            <line
              x1={0}
              x2={plotWidth}
              y1={y(coverLine.value)}
              y2={y(coverLine.value)}
              className={COVER_STROKE}
              strokeWidth={1.5}
              strokeDasharray={coverLine.basis === "thermal-shift" ? "6 4" : "2 4"}
            />
            <text x={4} y={y(coverLine.value) - 4} className={cn(COVER_TEXT, "text-[10px] font-medium")}>
              Cover line{coverLine.basis === "provisional" ? " (provisional)" : ""}
            </text>
          </g>
        ) : null}
        {shiftCd ? (
          <g>
            <line
              x1={(shiftCd - 1) * columnWidth}
              x2={(shiftCd - 1) * columnWidth}
              y1={0}
              y2={temperatureHeight}
              className={SHIFT_STROKE}
              strokeWidth={1.5}
              strokeDasharray="4 3"
            />
            <text
              x={(shiftCd - 1) * columnWidth + 4}
              y={temperatureHeight - 4}
              className={cn(SHIFT_TEXT, "text-[10px] font-medium")}
            >
              Temp shift
            </text>
          </g>
        ) : null}
        {gaps ? (
          <path d={gaps} fill="none" className={cn(TEMP_STROKE, "opacity-40")} strokeWidth={1.5} strokeDasharray="3 3" />
        ) : null}
        <path d={solid} fill="none" className={TEMP_STROKE} strokeWidth={2} strokeLinejoin="round" />
        {tempPoints.map((point) => (
          <g key={point.cycleDay}>
            {isHighTemp(point.cycleDay) ? (
              <circle
                cx={x(point.column)}
                cy={y(point.temperature)}
                r={7}
                fill="none"
                className={SHIFT_STROKE}
                strokeWidth={1.5}
              />
            ) : null}
            <circle
              cx={x(point.column)}
              cy={y(point.temperature)}
              r={activeIndex === point.column ? 5 : 3.5}
              className={cn(TEMP_FILL, "stroke-card")}
              strokeWidth={1.5}
            />
          </g>
        ))}
      </>
    );
  };

  const lastObservedIndex = model.days.reduce(
    (last, day, index) => (day.hasObservation ? index : last),
    0
  );

  return (
    <ChartFrame
      className={className}
      columns={columns}
      columnWidth={columnWidth}
      temperatureHeight={temperatureHeight}
      temperatureDomain={model.hasTemperatures ? model.temperatureDomain : null}
      temperatureEmpty={
        model.hasTemperatures || !model.temperatureEnabled
          ? null
          : { text: "No temperatures logged this cycle.", size: "compact" }
      }
      showPhaseStrip={stripSpans.length > 0}
      phaseStrip={phaseStrip}
      washes={washes}
      plot={model.hasTemperatures ? plot : undefined}
      lanes={model.lanes.map((lane) => ({
        key: lane,
        render: (index) => <LaneCell lane={lane} day={model.days[index]} />,
      }))}
      renderTooltip={(index) => <DayTooltip day={model.days[index]} model={model} />}
      defaultFocusIndex={lastObservedIndex}
      scrollRef={scrollRef}
      groupLabel="Cycle days — use arrow keys to move between days"
      headerLabel={model.partial ? "Date" : "CD"}
    />
  );
};

// ─── Multi-cycle overlay ────────────────────────────────────────────────────────────────────

interface OverlayProps {
  overlay: OverlayModel;
  /** Shown in place of the chart when no cycle can be placed with this alignment. */
  emptyText: string;
  /** Colour index for each series, by cycle id (stable when series are toggled off). */
  colorIndexById: Map<string, number>;
  hiddenIds: Set<string>;
  columnWidth: number;
  temperatureHeight: number;
  scrollRef?: React.Ref<HTMLDivElement>;
  className?: string;
}

const ANCHOR_HEADER_CLASS = {
  cd1: undefined,
  peak: "rounded-sm bg-violet-700 text-white",
  shift: "rounded-sm bg-orange-500 text-white",
} as const;

const ANCHOR_WASH_CLASS = {
  cd1: "",
  peak: "bg-violet-600/15 dark:bg-violet-400/15",
  shift: "bg-orange-500/15 dark:bg-orange-400/15",
} as const;

export const SeriesSwatch = ({ index }: { index: number }) => {
  const color = overlayColor(index);
  return (
    <svg width="22" height="8" viewBox="0 0 22 8" aria-hidden className="flex-none">
      <line x1="1" x2="21" y1="4" y2="4" className={color.stroke} strokeWidth={2} strokeDasharray={color.dash} />
    </svg>
  );
};

const OverlayTooltip = ({
  overlay,
  xValue,
  colorIndexById,
  hiddenIds,
}: {
  overlay: OverlayModel;
  xValue: number;
  colorIndexById: Map<string, number>;
  hiddenIds: Set<string>;
}) => (
  <div className="space-y-1.5">
    <p className="font-semibold">{describeOverlayX(xValue, overlay.align)}</p>
    <ul className="space-y-1">
      {overlay.series
        .filter((series) => !series.excluded && !hiddenIds.has(series.cycleId))
        .map((series) => {
          const point = series.points.find((p) => p.x === xValue);
          return (
            <li key={series.cycleId} className="flex items-center gap-1.5">
              <SeriesSwatch index={colorIndexById.get(series.cycleId) ?? 0} />
              <span className="min-w-0 flex-1 truncate text-muted-foreground">{series.label}</span>
              <span className="font-medium tabular-nums">
                {point ? formatTemperature(point.temperature, "°F") : "—"}
              </span>
            </li>
          );
        })}
    </ul>
  </div>
);

export const OverlayCycleChart = ({
  overlay,
  emptyText,
  colorIndexById,
  hiddenIds,
  columnWidth,
  temperatureHeight,
  scrollRef,
  className,
}: OverlayProps) => {
  const count = overlayColumnCount(overlay);
  const columns: ChartColumn[] = Array.from({ length: count }, (_, index) => {
    const xValue = overlay.xMin + index;
    return {
      key: String(xValue),
      header: formatOverlayX(xValue, overlay.align),
      headerClassName: xValue === 0 ? ANCHOR_HEADER_CLASS[overlay.align] : undefined,
      ariaLabel: describeOverlayColumn(overlay, xValue, hiddenIds),
    };
  });

  const visible = overlay.series.filter(
    (series) => !series.excluded && !hiddenIds.has(series.cycleId) && series.points.length
  );

  const washes =
    overlay.align !== "cd1" ? (
      <div
        className={cn("absolute inset-y-0", ANCHOR_WASH_CLASS[overlay.align])}
        style={{ left: (0 - overlay.xMin) * columnWidth, width: columnWidth }}
      />
    ) : null;

  if (!visible.length) {
    // Same outer height as the chart, so switching into this state doesn't move the page.
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-lg border bg-card px-6 text-center text-sm text-muted-foreground",
          className
        )}
        style={{ height: HEADER_HEIGHT + temperatureHeight + 2 }}
      >
        {emptyText}
      </div>
    );
  }

  const plot = (x: (column: number) => number, y: YScale, activeIndex: number | null) => (
    <>
      {/* Draw the newest cycle (first) last so it sits on top. */}
      {[...visible].reverse().map((series) => {
        const colorIndex = colorIndexById.get(series.cycleId) ?? 0;
        const color = overlayColor(colorIndex);
        const points = series.points.map((p) => ({ column: p.x - overlay.xMin, temperature: p.temperature }));
        const { solid, gaps } = buildLinePaths(points, x, y);
        return (
          <g key={series.cycleId}>
            <path
              d={`${solid} ${gaps}`}
              fill="none"
              className={color.stroke}
              strokeWidth={colorIndex === 0 ? 2.25 : 1.75}
              strokeDasharray={color.dash}
              strokeLinejoin="round"
            />
            {series.points.map((p) => {
              const column = p.x - overlay.xMin;
              return (
                <circle
                  key={p.cycleDay}
                  cx={x(column)}
                  cy={y(p.temperature)}
                  r={activeIndex === column ? 4.5 : 2.75}
                  className={cn(color.fill, "stroke-card")}
                  strokeWidth={1}
                />
              );
            })}
          </g>
        );
      })}
    </>
  );

  return (
    <ChartFrame
      className={className}
      columns={columns}
      columnWidth={columnWidth}
      temperatureHeight={temperatureHeight}
      temperatureDomain={overlay.temperatureDomain}
      showPhaseStrip={false}
      washes={washes}
      plot={plot}
      renderTooltip={(index) => (
        <OverlayTooltip
          overlay={overlay}
          xValue={overlay.xMin + index}
          colorIndexById={colorIndexById}
          hiddenIds={hiddenIds}
        />
      )}
      defaultFocusIndex={overlay.align === "cd1" ? 0 : -overlay.xMin}
      scrollRef={scrollRef}
      groupLabel="Compared cycle days — use arrow keys to move between days"
      headerLabel={overlay.align === "cd1" ? "CD" : overlay.align === "peak" ? "±Peak" : "±Shift"}
    />
  );
};
